import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, FileText, Trash2, FolderOpen, GitCompare, Plus, Lock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { useToast } from "../hooks/use-toast";
import { listComputations, deleteComputation } from "../lib/api";
import { fmtRM } from "../lib/taxConfig";

const SavedComputations = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [showCompare, setShowCompare] = useState(false);

  const companyName = (() => {
    try {
      const cs = JSON.parse(localStorage.getItem("ibizz_companies_v2") || "[]");
      return cs.find((c) => c.id === id)?.name || "";
    } catch (e) {
      return "";
    }
  })();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listComputations(id);
      setRows(data);
    } catch (e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = (compId) =>
    setSelected((s) => (s.includes(compId) ? s.filter((x) => x !== compId) : [...s, compId]));

  const remove = async (compId) => {
    try {
      await deleteComputation(compId);
      setSelected((s) => s.filter((x) => x !== compId));
      toast({ title: "Computation deleted" });
      load();
    } catch (e) {
      toast({ title: "Delete failed", variant: "destructive" });
    }
  };

  const compareRows = rows.filter((r) => selected.includes(r.id));

  return (
    <div className="max-w-[1100px] mx-auto">
      <button onClick={() => navigate("/company")} className="flex items-center gap-1 text-[#1877c9] hover:underline text-sm mb-3">
        <ArrowLeft size={16} /> Back to Companies
      </button>

      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded bg-[#1877c9] text-white flex items-center justify-center">
            <FolderOpen size={20} />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-800">Saved Computations</h1>
            <p className="text-sm text-gray-500">{companyName || "Company"} — past tax computations</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            disabled={selected.length < 2}
            onClick={() => setShowCompare((v) => !v)}
            className="gap-2 border-[#1877c9] text-[#1877c9] hover:bg-[#eef4fb]"
          >
            <GitCompare size={15} /> Compare ({selected.length})
          </Button>
          <Button onClick={() => navigate(`/company/${id}/tax`)} className="bg-[#1877c9] hover:bg-[#0f5ea3] gap-2">
            <Plus size={15} /> New
          </Button>
        </div>
      </div>

      {showCompare && compareRows.length >= 2 && (
        <div className="bg-white border border-gray-200 rounded-sm shadow-sm p-5 mb-5 overflow-x-auto">
          <h3 className="font-semibold text-gray-800 mb-3">Comparison</h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b border-gray-100">
                <th className="py-2 pr-4 font-medium">Metric</th>
                {compareRows.map((r) => (
                  <th key={r.id} className="py-2 px-3 font-medium text-gray-700">YA {r.ya}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                ["Chargeable income", "chargeableIncome"],
                ["Gross tax", "grossTax"],
                ["Tax paid (CP204)", "taxPaid"],
                ["Balance", "balance"],
              ].map(([label, key]) => (
                <tr key={key} className="border-b border-gray-50">
                  <td className="py-2 pr-4 text-gray-600">{label}</td>
                  {compareRows.map((r) => (
                    <td key={r.id} className="py-2 px-3 font-mono text-gray-800">
                      {fmtRM(Math.abs(r.results?.[key] || 0))}
                      {key === "balance" && (
                        <span className={`ml-1 text-xs ${r.results?.balance < 0 ? "text-green-600" : "text-[#1877c9]"}`}>
                          {r.results?.balanceLabel}
                        </span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-sm shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#f7f9fc] text-gray-600 text-left">
              <th className="px-4 py-3 w-10"></th>
              <th className="px-4 py-3 font-medium">YA</th>
              <th className="px-4 py-3 font-medium">Chargeable Income</th>
              <th className="px-4 py-3 font-medium">Gross Tax</th>
              <th className="px-4 py-3 font-medium">Balance</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Saved</th>
              <th className="px-4 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-gray-100 hover:bg-[#f9fbfe]">
                <td className="px-4 py-3">
                  <Checkbox checked={selected.includes(r.id)} onCheckedChange={() => toggle(r.id)} />
                </td>
                <td className="px-4 py-3 font-medium text-gray-800">{r.ya}</td>
                <td className="px-4 py-3 font-mono text-gray-700">{fmtRM(r.results?.chargeableIncome)}</td>
                <td className="px-4 py-3 font-mono text-gray-700">{fmtRM(r.results?.grossTax)}</td>
                <td className="px-4 py-3 font-mono">
                  {fmtRM(Math.abs(r.results?.balance || 0))}
                  <span className={`ml-1 text-xs ${r.results?.balance < 0 ? "text-green-600" : "text-[#1877c9]"}`}>
                    {r.results?.balanceLabel}
                  </span>
                </td>
                <td className="px-4 py-3">
                  {r.locked ? (
                    <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid={`status-submitted-${r.id}`}>
                      <Lock size={12} /> Submitted
                    </span>
                  ) : (
                    <span className="inline-flex items-center text-xs font-medium px-2 py-1 rounded bg-gray-100 text-gray-500 border border-gray-200" data-testid={`status-draft-${r.id}`}>
                      Draft
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-gray-500 text-xs">
                  {r.createdAt ? new Date(r.createdAt).toLocaleString("en-MY") : "-"}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      onClick={() => navigate(`/company/${id}/tax?load=${r.id}`)}
                      title="Reopen"
                      className="p-1.5 text-gray-500 hover:text-[#1877c9] hover:bg-blue-50 rounded"
                    >
                      <FileText size={16} />
                    </button>
                    <button
                      onClick={() => remove(r.id)}
                      title="Delete"
                      className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-gray-400">
                  No saved computations yet. Create one from the Tax Computation page.
                </td>
              </tr>
            )}
            {loading && (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-gray-400">Loading...</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SavedComputations;
